import type {
  Invoice,
  InvoiceList,
  PaymentsLog,
  PaymentsLogDetail,
} from "@/api/model";
import { PaymentsLogStatusEnum, ScheduledPaymentStatusEnum } from "@/api/model";
import type {
  InvoiceStatus,
  InvoiceStatusColors,
  InvoiceStatusDisplay,
  PaymentsLogFilters,
} from "@/types";
import type { RouteLocationRaw } from "vue-router";

import {
  format,
  startOfWeek,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
} from "date-fns";

import {
  ACTIVE_INVOICE,
  ACTIVE_INVOICE_DISPLAY,
  CANCELLED_INVOICE,
  CANCELLED_INVOICE_DISPLAY,
  DRAFT_INVOICE,
  DRAFT_INVOICE_DISPLAY,
  PAID_INVOICE,
  PAID_INVOICE_DISPLAY,
  PARTIALLY_PAID_INVOICE,
  PARTIALLY_PAID_INVOICE_DISPLAY,
  PAST_DUE_INVOICE,
  PAST_DUE_INVOICE_DISPLAY,
  UNKNOWN_INVOICE_DISPLAY,
  PAYMENT_STATUS_COLOR_SUCCESS,
  PAYMENT_STATUS_COLOR_PENDING,
  PAYMENT_STATUS_COLOR_ERROR,
  PAYMENT_STATUS_COLOR_NEUTRAL,
  PAYMENT_STATUS_COLOR_DEFAULT,
  TERMINAL_PAYMENT_SOURCE_TYPE,
} from "./constants";
import { BLACK, BLUE, GREEN, RED } from "@/shared/constants/colors";
import type { AdminV1OrganizationsPaymentsLogStatisticsRetrieveParams } from "@/api/model";
import { ROUTE_NAME } from "@/shared/constants/routes";

export const getInvoiceStatusColor = (
  status: InvoiceStatus
): InvoiceStatusColors => {
  switch (status) {
    case ACTIVE_INVOICE:
      return BLUE;
    case PAID_INVOICE:
    case PARTIALLY_PAID_INVOICE:
      return GREEN;
    case PAST_DUE_INVOICE:
      return RED;
    default:
      return BLACK;
  }
};

export const getFormattedInvoiceStatus = (
  status: InvoiceStatus
): InvoiceStatusDisplay => {
  switch (status) {
    case ACTIVE_INVOICE:
      return ACTIVE_INVOICE_DISPLAY;
    case PAID_INVOICE:
      return PAID_INVOICE_DISPLAY;
    case PAST_DUE_INVOICE:
      return PAST_DUE_INVOICE_DISPLAY;
    case DRAFT_INVOICE:
      return DRAFT_INVOICE_DISPLAY;
    case PARTIALLY_PAID_INVOICE:
      return PARTIALLY_PAID_INVOICE_DISPLAY;
    case CANCELLED_INVOICE:
      return CANCELLED_INVOICE_DISPLAY;
    default:
      return UNKNOWN_INVOICE_DISPLAY;
  }
};

export const getFormattedTitle = (data: Invoice | InvoiceList) => {
  if ("invoice_title" in data && data.invoice_title) {
    return data.invoice_title;
  }

  if ("invoice_line_item_names" in data && data.invoice_line_item_names) {
    const lineItemNames = data.invoice_line_item_names;
    const firstLineItem = lineItemNames[0];
    return firstLineItem || "N/A";
  }

  if ("items" in data && data.items && data.items.length > 0 && data.items[0]) {
    return data.items[0].item_product_name || "N/A";
  }

  return "N/A";
};

export const getLineItemCount = (data: InvoiceList) => {
  const lineItemNames = data.invoice_line_item_names;
  const lineItemsLength = lineItemNames.length - 1;

  if (lineItemsLength > 0) {
    return `+ ${lineItemsLength}`;
  }
};

export const getFormattedCreatedAt = (dateString: string | Date) => {
  return format(new Date(dateString), "MMMM dd, yyyy");
};

export function getDateRangeParams(
  rangeValue: string
): AdminV1OrganizationsPaymentsLogStatisticsRetrieveParams | undefined {
  const today = new Date();
  const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

  switch (rangeValue) {
    case "week-to-date":
      return {
        start_date: formatDate(startOfWeek(today, { weekStartsOn: 0 })),
        end_date: formatDate(today),
      };
    case "month-to-date":
      return {
        start_date: formatDate(startOfMonth(today)),
        end_date: formatDate(today),
      };
    case "year-to-date":
      return {
        start_date: formatDate(startOfYear(today)),
        end_date: formatDate(today),
      };
    case "last-7-days":
      return {
        start_date: formatDate(subDays(today, 7)),
        end_date: formatDate(today),
      };
    case "last-30-days":
      return {
        start_date: formatDate(subDays(today, 30)),
        end_date: formatDate(today),
      };
    case "last-3-months":
      return {
        start_date: formatDate(subMonths(today, 3)),
        end_date: formatDate(today),
      };
    case "last-6-months":
      return {
        start_date: formatDate(subMonths(today, 6)),
        end_date: formatDate(today),
      };
    case "last-12-months":
      return {
        start_date: formatDate(subMonths(today, 12)),
        end_date: formatDate(today),
      };
    case "all-time":
      return undefined;
    default:
      return undefined;
  }
}

export {
  dateStringToLocalDate,
  localDateToDateString,
} from "@/shared/utils/helpers";

export const formatPaymentMethod = (method: string | null): string => {
  if (!method) return "N/A";

  const brandAndDigits = method.match(/(\w+)\s+(\d{4})$/i);
  if (brandAndDigits && brandAndDigits[1] && brandAndDigits[2]) {
    const brandRaw = brandAndDigits[1];
    const brand =
      brandRaw.toUpperCase() === "ACH"
        ? "ACH"
        : brandRaw.charAt(0).toUpperCase() + brandRaw.slice(1).toLowerCase();
    return `${brand} •••• ${brandAndDigits[2]}`;
  }

  return method.toUpperCase() === "ACH"
    ? "ACH"
    : method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
};

export const formatPaymentStatus = (status: string): string => {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getPaymentStatusColor = (status: string): string => {
  if (status === PaymentsLogStatusEnum.PAID) {
    return PAYMENT_STATUS_COLOR_SUCCESS;
  } else if (
    status === PaymentsLogStatusEnum.SCHEDULED ||
    status === PaymentsLogStatusEnum.PROCESSING
  ) {
    return PAYMENT_STATUS_COLOR_PENDING;
  } else if (
    status === PaymentsLogStatusEnum.FAILED ||
    status === PaymentsLogStatusEnum.PAST_DUE
  ) {
    return PAYMENT_STATUS_COLOR_ERROR;
  } else if (
    status === PaymentsLogStatusEnum.REFUNDED ||
    status === PaymentsLogStatusEnum.REVERSED ||
    status === ScheduledPaymentStatusEnum.CANCELED
  ) {
    return PAYMENT_STATUS_COLOR_NEUTRAL;
  }

  return PAYMENT_STATUS_COLOR_DEFAULT;
};

type SubscriberMembershipInput = Pick<
  PaymentsLog | PaymentsLogDetail,
  "source_type" | "source_id"
>;

// Returns the subscriber membership id when the payment is sourced from a
// membership. Used by the refund action in both PaymentsLog and the detail
// drawer; the membership id is the parent record we hit for refund history.
export const getSubscriberMembershipId = (
  payment: SubscriberMembershipInput
): string | undefined => {
  if (payment.source_type === "subscriber_membership" && payment.source_id) {
    return payment.source_id;
  }
  return undefined;
};

export const BOOKING_SESSION_SOURCE_TYPE = "booking_session";

export const isTerminalPayment = (
  payment: Pick<PaymentsLog | PaymentsLogDetail, "source_type">
): boolean => payment.source_type === TERMINAL_PAYMENT_SOURCE_TYPE;

/** Source cell text: terminal payments show "–" when untitled; other rows keep "N/A". */
export const getPaymentSourceLabel = (
  payment: Pick<PaymentsLog | PaymentsLogDetail, "source_type" | "source_name">
): string => payment.source_name || (isTerminalPayment(payment) ? "–" : "N/A");

/**
 * When a payment's source is a service booking, `source_id` is the composite
 * `bookedServiceId` the new Booking Detail Drawer loads from. Returns it so the
 * Source link can open the drawer instead of the old full-page route.
 */
export const getBookingSourceId = (
  payment: Pick<PaymentsLog | PaymentsLogDetail, "source_type" | "source_id">
): string | null =>
  payment.source_type === BOOKING_SESSION_SOURCE_TYPE && payment.source_id
    ? payment.source_id
    : null;

type PaymentSourceRouteInput = Pick<
  PaymentsLog | PaymentsLogDetail,
  "source_type" | "source_id" | "type"
>;

export const getPaymentSourceRoute = (
  payment: PaymentSourceRouteInput
): RouteLocationRaw | null => {
  if (!payment.source_type || !payment.source_id) {
    return null;
  }

  const sourceType = payment.source_type;
  const sourceId = payment.source_id;
  const normalizedType = payment.type?.toLowerCase();
  const isRegistrationPayment = normalizedType === "registration";

  switch (sourceType) {
    case "subscriber_registration":
      return {
        name: ROUTE_NAME.PAYMENT_REGISTRANT_VIEW,
        params: { id: sourceId },
      };
    case "subscriber_membership":
      return isRegistrationPayment
        ? {
            name: ROUTE_NAME.PAYMENT_REGISTRANT_VIEW,
            params: { id: sourceId },
          }
        : {
            name: ROUTE_NAME.PAYMENT_MEMBERSHIPS_MEMBER_VIEW,
            params: { id: sourceId },
          };
    case "booking_session":
      return {
        name: ROUTE_NAME.BOOKING_DETAIL,
        params: { bookedServiceId: sourceId },
      };
    case "invoice":
      return { name: ROUTE_NAME.VIEW_INVOICE, params: { id: sourceId } };
    default:
      return null;
  }
};

/** Upcoming (scheduled) payments read soonest first; paid, most recent first. */
export const defaultPaymentsOrderingFor = (
  filter: PaymentsLogFilters
): string => (filter === "scheduled" ? "scheduled_at" : "-payment_date");
